/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { HierarchicalEntityChild } from 'src/luis/projects/entities/types/HierarchicalEntity';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { HierarchicalEntityChildTable } from 'src/luis/verticals/entityDetails/components/hierarchicalEntityDetails/HierarchicalEntityChildTable';
import { HierarchicalEntityChildTableCommandBar } from 'src/luis/verticals/entityDetails/components/hierarchicalEntityDetails/HierarchicalEntityChildTableCommandBar';

export type HierarchicalEntityDetailsProps = {
    /**
     * The hierarchical entity to display details for.
     */
    entityStore: HierarchicalEntityStore;
};

const messages = defineMessages({
    childrenSectionTitle: {
        id: 'hierarchicalEntityDetails.hierarchicalEntityDetails.childrenSectionTitle',
        defaultMessage: 'Children'
    },
    deleteChildModalTitle: {
        id: 'hierarchicalEntityDetails.hierarchicalEntityDetails.deleteChildModalTitle',
        defaultMessage: 'Delete child/children?'
    },
    deleteChildModalDescription: {
        id: 'hierarchicalEntityDetails.hierarchicalEntityDetails.deleteChildModalDescription',
        defaultMessage: 'Are you sure you want to delete this child/children?'
    }
});

/**
 * Represents the details for a hierarchical entity which includes
 * listing and controlling the entity children.
 *
 * @param props The input properties for the entity details.
 */
export const HierarchicalEntityDetails = observer((props: HierarchicalEntityDetailsProps) => {
    const { entityStore } = props;
    const { selection, selectedItems } = useSelection<HierarchicalEntityChild>();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const versionedAppId = useCurrentVersionedAppId();
    const [isDeleteChildrenModalHidden, setIsDeleteChildrenModalHidden] = React.useState(true);

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    const onDeleteChildren = (): void => {
        setIsDeleteChildrenModalHidden(true);
        selection.setAllSelected(false);
        dispatcher.dispatch('deleteChildrenEntityForHierarchicalEntity', {
            ...versionedAppId,
            id: entityStore.id,
            childIds: selectedItems.map(c => c.id)
        });
    };

    const onRenameChild = (childId: string, childName: string): void => {
        selection.setAllSelected(false);
        dispatcher.dispatch('renameHierarchicalChildEntity', {
            ...versionedAppId,
            id: entityStore.id,
            childId,
            name: childName
        });
    };

    return (
        <Stack>
            <HierarchicalEntityChildTableCommandBar
                selectedItems={selectedItems}
                onDeleteChildren={() => setIsDeleteChildrenModalHidden(false)}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />

            <HierarchicalEntityChildTable items={entityStore.children.slice()} selection={selection} onRenameChild={onRenameChild} />

            <DeletePromptModal
                hidden={isDeleteChildrenModalHidden}
                onDelete={onDeleteChildren}
                dialogContentProps={{
                    title: localize(messages.deleteChildModalTitle),
                    subText: localize(messages.deleteChildModalDescription)
                }}
                onDismiss={() => setIsDeleteChildrenModalHidden(true)}
            />
        </Stack>
    );
});
